import { useEffect, useCallback, useRef } from "react";
import { format } from "date-fns";
import axios from "axios";

import { useErrorHandler } from "../../../../../../../../../_metronic/helpers/ErrorHandler";
import { useViewImageContext } from "../../ViewImageContext";
import { useMapLegendContext } from "../../../Helpers/MapLegend";
import { useMapLayersV2 } from "../../../../../../Map/MapLayersV2Context";
import { viewImageState } from "../../ViewImageState";
import { getLayerBbox, parseIndexRangeHeader, parseSingleIndexLegendHeaders, recoverCachedImageLayer } from "../imageLayerPayload";
import {
    getApiLayerForImageType,
    getHdAuthorizationId,
    getMapLayerImageType,
    isHdImageType,
} from "../../imageTypeUtils";
import {
    isCanonicalPlanetHdLayerRequest,
    requestRemoteSensingLayer,
    requestRemoteSensingTiff,
} from "../../remoteSensingLayerRequest";
import {
    useImageLayerAttempt,
    useStableImageLayerInputs,
} from "../../useImageLayerAttempt";

const parseHeaders = (totalHeaders) => {
    if (!totalHeaders || totalHeaders.length < 1) {
        return null;
    }

    const ha = {};
    let totalIntervals = {};
    let totalHaIntervals = 0;

    totalHeaders.forEach((headerItem) => {
        const intervals = parseIndexRangeHeader(headerItem?.headers);
        if (!intervals) {
            return;
        }

        const gis = headerItem.id;

        if (!ha[gis]) {
            ha[gis] = {
                'total': 0,
                'count': 0
            };
        }

        intervals.forEach((e) => {
            if (!totalIntervals[e['cluster']]) {
                totalIntervals[e['cluster']] = {
                    'total': 0,
                    'count': 0
                };
            }

            totalIntervals[e['cluster']]['count'] += 1;
            totalIntervals[e['cluster']]['total'] += parseFloat(e['ha']);
            totalHaIntervals += parseFloat(e['ha']);
            ha[gis]['total'] += parseFloat(e['ha']);
        });

        ha[gis]['count'] += 1;
    })

    if (totalHaIntervals <= 0) {
        return null;
    }

    const intervals = [];
    Object.keys(totalIntervals).forEach(key => {
        const haInterval = totalIntervals[key]['total'] / totalIntervals[key]['count'];
        const percentage = (totalIntervals[key]['total'] * 100) / totalHaIntervals;
        intervals.push({
            cluster: key,
            ha: haInterval,
            percentage
        });
    });

    let totalHa = 0;
    Object.keys(ha).forEach(key => {
        totalHa += (ha[key]['total'] / ha[key]['count']);
    });

    return {'x-index-range': JSON.stringify(intervals), 'x-total-ha': totalHa};
};


const EVI = ({ acquisition = null, land, selectedDate, imageType = "evi" }) => {
    const { errorHandler } = useErrorHandler();
    const mapLayers = useMapLayersV2();
    const viewImageContext = useViewImageContext();
    const mapLegendContext = useMapLegendContext();
    const executing = useRef(false);
    const mounted = useRef(true);
    const activeRequest = useRef(null);
    const activeAttemptKey = useRef(null);
    const activeRequestKey = useRef(null);
    const activeLoadId = useRef(0);
    const activeLoadKey = useRef(null);
    const activeObjectUrl = useRef(null);
    const mapLayersRef = useRef(mapLayers);
    const mapLegendContextRef = useRef(mapLegendContext);
    const viewImageContextRef = useRef(viewImageContext);
    const enhancedFlag = Boolean(mapLegendContext.enhanced[land.land_id]);
    const mapLayerImageType = getMapLayerImageType(imageType);
    const apiLayer = getApiLayerForImageType(imageType);
    const date = format(selectedDate, 'yyyy-MM-dd');
    const { cacheOptions, stableAcquisition } = useStableImageLayerInputs({
        acquisition,
        date,
        enhanced: enhancedFlag,
        imageType: mapLayerImageType,
        land,
    });
    const retryVersion = viewImageContext.getImageLayerLoadState(land.land_id).retryVersion;
    const { attemptKey, handleRequest, requestKey } = useImageLayerAttempt({
        cacheOptions,
        errorHandler,
        landId: land.land_id,
        retryVersion,
    });

    mapLayersRef.current = mapLayers;
    mapLegendContextRef.current = mapLegendContext;
    viewImageContextRef.current = viewImageContext;

    const deleteOldImage = useCallback(() => {
        mapLayersRef.current.land.deleteImage({ landId: land.land_id, imageType: mapLayerImageType });
    }, [land.land_id, mapLayerImageType]);

    const revokeActiveObjectUrl = useCallback(() => {
        if (activeObjectUrl.current) {
            window.URL.revokeObjectURL(activeObjectUrl.current);
            activeObjectUrl.current = null;
        }
    }, []);

    const paintLayer = useCallback((payload) => {
        const url = window.URL || window.webkitURL;

        deleteOldImage();
        revokeActiveObjectUrl();

        const src = url.createObjectURL(payload.blob);
        activeObjectUrl.current = src;

        mapLayersRef.current.land.addImage({
            landId: land.land_id,
            imageType: mapLayerImageType,
            imageUrl: src,
            polygon: land.polygons,
            bbox: payload.bbox,
            tif: payload.tif
        });

        if (payload.tif) {
            viewImageContextRef.current.addLandTiff(land.land_id, payload.tif, payload.cacheOptions);
        }

        mapLegendContextRef.current.changeBasicLegend(land.land_id, payload.legend);
    }, [deleteOldImage, land.land_id, land.polygons, mapLayerImageType, revokeActiveObjectUrl]);

    const loadImage = useCallback(async () => {
        if (activeLoadKey.current !== attemptKey) {
            activeLoadKey.current = attemptKey;
            activeLoadId.current += 1;
        }
        const loadId = activeLoadId.current;
        const cachedLayer = recoverCachedImageLayer(
            viewImageState.getLandImageLayer(land.land_id, cacheOptions), land.polygons
        );

        if (cachedLayer) {
            mapLayersRef.current.fit(land.land_id);
            paintLayer(cachedLayer);
            viewImageContextRef.current.setImageLayerReady(land.land_id, requestKey);
            return;
        }

        if (!viewImageContextRef.current.claimImageLayerAttempt(land.land_id, attemptKey)) {
            return;
        }

        executing.current = true;

        const canonicalPlanetHd = isCanonicalPlanetHdLayerRequest({acquisition: stableAcquisition, imageType});
        const authorized = canonicalPlanetHd
            ? {access_id: stableAcquisition.accessId}
            : isHdImageType(imageType)
            ? await viewImageContextRef.current.requestHdAccess({
                land,
                operation: "image_hd",
                context: {
                    image_type: imageType,
                    date,
                },
                onConfirmed: (access) => access,
            })
            : true;
        const hdAuthorizationId = getHdAuthorizationId(authorized);

        if (!mounted.current) {
            viewImageContextRef.current.releaseImageLayerAttempt(land.land_id, attemptKey);
            executing.current = false;
            return;
        }

        if (activeLoadId.current !== loadId) {
            viewImageContextRef.current.releaseImageLayerAttempt(land.land_id, attemptKey);
            executing.current = false;
            return;
        }

        if (!authorized) {
            viewImageContextRef.current.releaseImageLayerAttempt(land.land_id, attemptKey);
            viewImageContextRef.current.setImageLayerLoading(land.land_id, requestKey);
            viewImageContextRef.current.setImageLayerError(land.land_id, requestKey, {error_type: 'planet_hd_access_unavailable'});
            executing.current = false;
            return;
        }

        if (activeRequest.current) {
            activeRequest.current.cancel?.("image-request-replaced");
            viewImageContextRef.current.releaseImageLayerAttempt(land.land_id, activeAttemptKey.current);
            viewImageContextRef.current.cancelImageLayerLoad(land.land_id, activeRequestKey.current);
        }
        const cancelTokenRequest = axios.CancelToken.source();
        activeRequest.current = cancelTokenRequest;
        activeAttemptKey.current = attemptKey;
        activeRequestKey.current = requestKey;
        deleteOldImage();
        revokeActiveObjectUrl();
        mapLegendContextRef.current.clearBasicLegend(land.land_id);
        viewImageContextRef.current.setImageLayerLoading(land.land_id, requestKey);
        viewImageContextRef.current.setLoading(true);
        try {
            mapLayersRef.current.fit(land.land_id);
            const res = await handleRequest(requestRemoteSensingLayer({
                acquisition: stableAcquisition,
                cancelTokenRequest,
                imageType,
                land,
                layer: apiLayer,
                options: {
                    date,
                    enhanced: enhancedFlag,
                    ...(hdAuthorizationId ? {hd_authorization_id: hdAuthorizationId} : {}),
                },
            }));

            if (!mounted.current || activeLoadId.current !== loadId || activeRequest.current !== cancelTokenRequest) {
                if (activeRequest.current === cancelTokenRequest) {
                    activeRequest.current = null;
                    activeAttemptKey.current = null;
                    activeRequestKey.current = null;
                    viewImageContextRef.current.releaseImageLayerAttempt(land.land_id, attemptKey);
                    viewImageContextRef.current.cancelImageLayerLoad(land.land_id, requestKey);
                    if (mounted.current) {
                        viewImageContextRef.current.setLoading(false);
                    }
                }
                executing.current = false;
                return;
            }

            if (!res?.ok) {
                if (res?.error_type === 'cancel_tpoken') {
                    viewImageContextRef.current.releaseImageLayerAttempt(land.land_id, attemptKey);
                    viewImageContextRef.current.cancelImageLayerLoad(land.land_id, requestKey);
                } else {
                    viewImageContextRef.current.setImageLayerError(land.land_id, requestKey, res);
                }

                activeRequest.current = null;
                activeAttemptKey.current = null;
                activeRequestKey.current = null;
                viewImageContextRef.current.setLoading(false);
                executing.current = false;
                return;
            }

            const tif = await handleRequest(requestRemoteSensingTiff({
                acquisition: stableAcquisition,
                cancelTokenRequest,
                imageType,
                land,
                layer: apiLayer,
                options: {
                    date,
                    ...(hdAuthorizationId ? {hd_authorization_id: hdAuthorizationId} : {}),
                },
            }));

            if (!mounted.current || activeLoadId.current !== loadId || activeRequest.current !== cancelTokenRequest) {
                if (activeRequest.current === cancelTokenRequest) {
                    activeRequest.current = null;
                    activeAttemptKey.current = null;
                    activeRequestKey.current = null;
                    viewImageContextRef.current.releaseImageLayerAttempt(land.land_id, attemptKey);
                    viewImageContextRef.current.cancelImageLayerLoad(land.land_id, requestKey);
                    if (mounted.current) {
                        viewImageContextRef.current.setLoading(false);
                    }
                }
                executing.current = false;
                return;
            }

            if (tif?.ok) {
                viewImageContextRef.current.addLandTiff(land.land_id, tif.data, cacheOptions);
            }

            const payload = {
                blob: res.data,
                bbox: getLayerBbox(res.headers, land.polygons),
                tif: tif?.data || null,
                legend: isHdImageType(imageType)
                    ? parseSingleIndexLegendHeaders(res.headers)
                    : parseHeaders([{id: land.land_id, date: selectedDate, headers: res.headers}]),
                cacheOptions,
            };
            if (payload.bbox) {
                viewImageState.addLandImageLayer(land.land_id, cacheOptions, payload);
                paintLayer(payload);
                viewImageContextRef.current.setImageLayerReady(land.land_id, requestKey);
            } else {
                viewImageContextRef.current.setImageLayerError(land.land_id, requestKey, {
                    error_type: 'invalid_image_coordinates',
                });
            }

        } catch (e) {
            if (e?.error_type === "cancel_tpoken") {
                viewImageContextRef.current.releaseImageLayerAttempt(land.land_id, attemptKey);
                viewImageContextRef.current.cancelImageLayerLoad(land.land_id, requestKey);
            } else {
                viewImageContextRef.current.setImageLayerError(land.land_id, requestKey, e);
            }

            if (mounted.current && e?.error_type !== "cancel_tpoken") {
                console.error(e);
            }
        }

        if (activeRequest.current === cancelTokenRequest) {
            activeRequest.current = null;
            activeAttemptKey.current = null;
            activeRequestKey.current = null;
        }

        if (mounted.current) {
            viewImageContextRef.current.setLoading(false);
        }
        executing.current = false;
    }, [apiLayer, attemptKey, cacheOptions, date, deleteOldImage, enhancedFlag, handleRequest, imageType, land, paintLayer, requestKey, revokeActiveObjectUrl, selectedDate, stableAcquisition])

    useEffect(() => {
        loadImage();
    }, [loadImage]);

    useEffect(() => {
        return () => {
            mounted.current = false;
            if (activeRequest.current) {
                activeRequest.current.cancel?.("image-tab-left");
                viewImageContextRef.current.releaseImageLayerAttempt(land.land_id, activeAttemptKey.current);
                viewImageContextRef.current.cancelImageLayerLoad(land.land_id, activeRequestKey.current);
            }
            activeLoadId.current += 1;
            viewImageContextRef.current.setLoading(false);
            mapLegendContextRef.current.clearBasicLegend(land.land_id);
            executing.current = false;
            deleteOldImage();
            revokeActiveObjectUrl();
        };
    }, [deleteOldImage, land.land_id, revokeActiveObjectUrl]);

    return (
        <>
        </>
    );
};

export default EVI;
